import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, postData } from "../../services/request"
import CvScreeningMessageComposer from "./CvScreeningMessageComposer"
import {
  apiErrorMessage,
  batchStatusClass,
  batchStatusLabel,
  formatScore,
  itemStatusLabel,
  recommendationClass,
  recommendationLabel,
  scoreBarColor,
} from "./cvScreeningUtils"

const GROUP_OPTIONS = [
  { value: "STRONG_FIT", label: "Strong fit" },
  { value: "POSSIBLE_FIT", label: "Possible fit" },
  { value: "WEAK_FIT", label: "Weak fit" },
]

const audienceLabel = (msg) => {
  if (msg.audience === "INDIVIDUAL") return "Individual"
  if (msg.audience === "SELECTED") return "Selected"
  if (msg.audience === "GROUP") return recommendationLabel(msg.group)
  return "All candidates"
}
const POLL_MS = 2000

const CvScreeningBatchPage = () => {
  const { batchId } = useParams()
  const [batch, setBatch] = useState(null)
  const [items, setItems] = useState([])
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [retrying, setRetrying] = useState(false)
  const [tab, setTab] = useState("candidates")
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [messages, setMessages] = useState([])
  const [composer, setComposer] = useState(null)
  const [sending, setSending] = useState(false)
  const [messageNotice, setMessageNotice] = useState("")

  const isRunning = batch && ["QUEUED", "PROCESSING"].includes(batch.status)
  const isDone = batch && ["COMPLETED", "PARTIAL", "FAILED"].includes(batch.status)

  const loadBatch = useCallback(async () => {
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}`
    const res = await getData(url, {}, {})
    if (res.error === "" && res.result?.status === 200) {
      setBatch(res.result.data?.data || null)
      return true
    }
    setError(apiErrorMessage(res, "Could not load batch"))
    return false
  }, [batchId])

  const loadItems = useCallback(async () => {
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/items`
    const res = await getData(url, {}, { limit: 100, sortBy: "score", sortDir: "desc" })
    if (res.error === "" && res.result?.status === 200) {
      setItems(res.result.data?.data?.items || [])
    }
  }, [batchId])

  const loadReport = useCallback(async () => {
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/report`
    const res = await getData(url, {}, {})
    if (res.error === "" && res.result?.status === 200) {
      setReport(res.result.data?.data || null)
    }
  }, [batchId])

  const loadMessages = useCallback(async () => {
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/messages`
    const res = await getData(url, {}, {})
    if (res.error === "" && res.result?.status === 200) {
      setMessages(res.result.data?.data?.messages || [])
    }
  }, [batchId])

  const refresh = useCallback(async () => {
    const ok = await loadBatch()
    if (!ok) return
    await loadItems()
  }, [loadBatch, loadItems])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError("")
      await refresh()
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [refresh])

  useEffect(() => {
    if (!isRunning) return undefined
    const t = setInterval(() => {
      refresh()
    }, POLL_MS)
    return () => clearInterval(t)
  }, [isRunning, refresh])

  useEffect(() => {
    if (isDone) loadReport()
  }, [isDone, loadReport, batch?.status, batch?.updatedAt])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  const progress = batch?.progress || { total: 0, processed: 0, failed: 0 }
  const pct =
    progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0

  const failedCount = useMemo(
    () => items.filter((i) => i.status === "FAILED").length || progress.failed || 0,
    [items, progress.failed]
  )

  const doneItems = useMemo(() => items.filter((i) => i.status === "DONE"), [items])
  const allDoneSelected =
    doneItems.length > 0 && doneItems.every((i) => selectedIds.has(i._id))

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAllDone = () => {
    setSelectedIds((prev) => {
      if (doneItems.length && doneItems.every((i) => prev.has(i._id))) {
        return new Set()
      }
      return new Set(doneItems.map((i) => i._id))
    })
  }

  const openComposer = (audience, extras = {}) => {
    setError("")
    setMessageNotice("")
    setComposer({ audience, group: extras.group || "", itemId: extras.itemId || "", candidateName: extras.candidateName || "" })
  }

  const handleSendMessages = async ({ subject, body }) => {
    if (!composer) return
    setSending(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/messages`
    const payload = {
      audience: composer.audience,
      group: composer.group || undefined,
      subject,
      body,
    }
    if (composer.audience === "SELECTED") {
      payload.itemIds = Array.from(selectedIds)
    }
    if (composer.audience === "INDIVIDUAL" && composer.itemId) {
      payload.itemIds = [composer.itemId]
    }
    const res = await postData(
      url,
      { "Content-Type": "application/json" },
      JSON.stringify(payload)
    )
    setSending(false)
    if (res.error === "" && (res.result?.status === 200 || res.result?.status === 201)) {
      const data = res.result.data?.data || {}
      const skipped = (data.skippedNames || []).join(", ")
      setMessageNotice(
        `${data.message || "Emails sent"}${skipped ? `. Skipped (no email): ${skipped}` : ""}`
      )
      setComposer(null)
      if (composer.audience === "SELECTED") setSelectedIds(new Set())
      await loadMessages()
      setTab("messages")
    } else {
      setError(apiErrorMessage(res, "Could not send emails"))
    }
  }

  const handleRetry = async () => {
    setRetrying(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/retry-failed`
    const res = await postData(url, { "Content-Type": "application/json" }, JSON.stringify({}))
    setRetrying(false)
    if (res.error === "" && res.result?.status === 200) {
      setBatch(res.result.data?.data || batch)
      await refresh()
    } else {
      setError(apiErrorMessage(res, "Retry failed"))
    }
  }

  const summary = report?.summaryReport || batch?.summaryReport || {}
  const topCandidates = report?.topCandidates || []
  const distribution = summary.scoreDistribution || { strong: 0, possible: 0, weak: 0 }
  const metricAverages = summary.metricAverages || []

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl rounded-xl border border-gray-200 bg-white p-10 text-center">
        <div className="mx-auto mb-3 h-8 w-8 animate-pulse-soft rounded-full bg-slate-200" />
        <p className="text-sm text-gray-500">Loading screening...</p>
      </div>
    )
  }

  if (!batch) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || "Batch not found"}
        </div>
        <Link to="/employer/cv-screening" className="mt-4 inline-block text-sm text-cyan-700">
          ← Back to screenings
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in">
      <div className="mb-4">
        <Link
          to="/employer/cv-screening"
          className="text-xs font-medium text-cyan-700 hover:text-cyan-900"
        >
          ← All screenings
        </Link>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 px-5 py-7 text-white shadow-sm sm:px-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 30%, rgba(0,165,230,0.5), transparent 40%)",
          }}
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-xl font-semibold tracking-tight sm:text-2xl">
                {batch.roleSnapshot?.title || "Untitled role"}
              </h2>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${batchStatusClass(
                  batch.status
                )}`}
              >
                {batchStatusLabel(batch.status)}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-200/90">
              {batch.sourceType === "JOB_POST" ? "From live job" : "Screening role"} ·{" "}
              {progress.total} CV{progress.total === 1 ? "" : "s"}
              {typeof summary.averageOverallScore === "number"
                ? ` · Avg ${formatScore(summary.averageOverallScore)}`
                : ""}
            </p>
          </div>
          {failedCount > 0 && !isRunning ? (
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="shrink-0 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-cyan-50 disabled:opacity-50"
            >
              {retrying ? "Retrying..." : `Retry ${failedCount} failed`}
            </button>
          ) : null}
        </div>

        {isRunning || (progress.total > 0 && pct < 100 && !isDone) ? (
          <div className="relative mt-6">
            <div className="mb-2 flex justify-between text-xs text-cyan-100/90">
              <span>
                {progress.processed}/{progress.total} processed
                {progress.failed ? ` · ${progress.failed} failed` : ""}
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/15">
              <div
                className="h-full rounded-full bg-cyan-400 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-2 text-[11px] text-slate-300">
              AI is reading and scoring each CV. This page updates automatically.
            </p>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {messageNotice ? (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {messageNotice}
        </div>
      ) : null}

      <div className="mt-5 flex gap-2 border-b border-gray-200">
        {[
          { key: "candidates", label: "Candidates" },
          { key: "report", label: "Batch report" },
          { key: "messages", label: `Messages${messages.length ? ` (${messages.length})` : ""}` },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-3 py-2.5 text-sm font-semibold transition ${
              tab === t.key
                ? "border-cyan-600 text-cyan-800"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "candidates" ? (
        <div className="mt-4">
          {doneItems.length ? (
            <div className="mb-3 flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={allDoneSelected}
                  onChange={toggleSelectAllDone}
                  className="h-4 w-4 rounded border-gray-300"
                />
                Select all screened ({doneItems.length})
                {selectedIds.size ? ` · ${selectedIds.size} selected` : ""}
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openComposer("SELECTED")}
                  disabled={!selectedIds.size}
                  className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-900 disabled:opacity-40"
                >
                  Email selected
                </button>
                <button
                  type="button"
                  onClick={() => openComposer("ALL")}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Email all
                </button>
                {GROUP_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => openComposer("GROUP", { group: opt.value })}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Email {opt.label.toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {!items.length ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
              {isRunning ? "Waiting for first results..." : "No CVs in this batch yet."}
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((item, idx) => (
                <li
                  key={item._id}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-cyan-200 hover:shadow-md animate-slide-up"
                  style={{ animationDelay: `${Math.min(idx, 10) * 30}ms` }}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-1 gap-3">
                      {item.status === "DONE" ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item._id)}
                          onChange={() => toggleSelected(item._id)}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300"
                          aria-label={`Select ${item.candidateName || item.originalName || "candidate"}`}
                        />
                      ) : (
                        <span className="mt-1 h-4 w-4 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {item.candidateName || item.originalName || "Candidate"}
                        </p>
                        {item.recommendation ? (
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${recommendationClass(
                              item.recommendation
                            )}`}
                          >
                            {recommendationLabel(item.recommendation)}
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                            {itemStatusLabel(item.status)}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-gray-500">
                        {item.originalName}
                        {item.candidateEmail ? ` · ${item.candidateEmail}` : " · No email on CV"}
                        {item.error ? ` · ${item.error}` : ""}
                      </p>
                      {typeof item.overallScore === "number" ? (
                        <div className="mt-2 max-w-xs">
                          <div className="mb-1 flex justify-between text-[11px] text-gray-500">
                            <span>Overall</span>
                            <span className="font-semibold text-gray-800">
                              {formatScore(item.overallScore)}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full ${scoreBarColor(item.overallScore)}`}
                              style={{ width: `${Math.min(100, item.overallScore)}%` }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {item.status === "DONE" ? (
                        <button
                          type="button"
                          onClick={() =>
                            openComposer("INDIVIDUAL", {
                              itemId: item._id,
                              candidateName: item.candidateName || item.originalName,
                            })
                          }
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                        >
                          Email
                        </button>
                      ) : null}
                      <Link
                        to={`/employer/cv-screening/${batchId}/items/${item._id}`}
                        className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-900"
                      >
                        View report
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : tab === "report" ? (
        <div className="mt-4 space-y-4 animate-slide-up">
          {!isDone && !summary.narrative ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
              Batch report appears when analysis finishes.
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  { label: "Strong fit", value: distribution.strong, group: "STRONG_FIT", tone: "text-emerald-700 bg-emerald-50 border-emerald-100" },
                  { label: "Possible fit", value: distribution.possible, group: "POSSIBLE_FIT", tone: "text-amber-800 bg-amber-50 border-amber-100" },
                  { label: "Weak fit", value: distribution.weak, group: "WEAK_FIT", tone: "text-slate-700 bg-slate-50 border-slate-200" },
                ].map((c) => (
                  <div
                    key={c.label}
                    className={`rounded-xl border px-4 py-4 ${c.tone}`}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
                      {c.label}
                    </p>
                    <p className="mt-1 text-2xl font-semibold">{c.value ?? 0}</p>
                    {c.value > 0 ? (
                      <button
                        type="button"
                        onClick={() => openComposer("GROUP", { group: c.group })}
                        className="mt-3 text-xs font-semibold underline-offset-2 hover:underline"
                      >
                        Email this group
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>

              {summary.narrative ? (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">AI summary</h3>
                  <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
                    {summary.narrative}
                  </p>
                  {summary.hiringRecommendations ? (
                    <>
                      <h4 className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Hiring recommendations
                      </h4>
                      <p className="mt-1 whitespace-pre-line text-sm text-gray-700">
                        {summary.hiringRecommendations}
                      </p>
                    </>
                  ) : null}
                </div>
              ) : null}

              {metricAverages.length ? (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">Metric averages</h3>
                  <ul className="mt-3 space-y-3">
                    {metricAverages.map((m) => {
                      const meta = (batch.metrics || []).find((x) => x.key === m.metricKey)
                      const avg = typeof m.average === "number" ? m.average : null
                      return (
                        <li key={m.metricKey}>
                          <div className="mb-1 flex justify-between text-xs text-gray-600">
                            <span>{meta?.label || m.metricKey}</span>
                            <span className="font-semibold text-gray-900">
                              {formatScore(avg)}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full ${scoreBarColor(avg)}`}
                              style={{ width: `${avg != null ? Math.min(100, avg) : 0}%` }}
                            />
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ) : null}

              {topCandidates.length ? (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">Top candidates</h3>
                  <ul className="mt-3 divide-y divide-gray-100">
                    {topCandidates.map((c, i) => (
                      <li
                        key={c._id}
                        className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            #{i + 1} {c.candidateName || c.originalName}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {recommendationLabel(c.recommendation)} · Score{" "}
                            {formatScore(c.overallScore)}
                          </p>
                        </div>
                        <Link
                          to={`/employer/cv-screening/${batchId}/items/${c._id}`}
                          className="shrink-0 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
                        >
                          Open
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3 animate-slide-up">
          {!messages.length ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
              No emails sent from this screening yet. Use Email all, a fit group, or selected candidates.
            </div>
          ) : (
            <ul className="space-y-2">
              {messages.map((msg, idx) => (
                <li
                  key={msg._id || idx}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {msg.subject || "Hiring update"}
                      </p>
                      <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{msg.body}</p>
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-gray-500">
                      <p className="font-semibold text-slate-700">{audienceLabel(msg)}</p>
                      <p>{msg.recipientCount || 0} sent</p>
                      {msg.skippedCount ? <p>{msg.skippedCount} skipped</p> : null}
                      {msg.failedCount ? <p>{msg.failedCount} failed</p> : null}
                      <p>{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ""}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <CvScreeningMessageComposer
        open={Boolean(composer)}
        onClose={() => !sending && setComposer(null)}
        audience={composer?.audience}
        group={composer?.group}
        selectedCount={selectedIds.size}
        candidateName={composer?.candidateName}
        roleTitle={batch.roleSnapshot?.title}
        sending={sending}
        error={composer ? error : ""}
        onSend={handleSendMessages}
      />
    </div>
  )
}

export default CvScreeningBatchPage
